/**
 * Reference Data → "Define a full Private Credit / Leveraged loan with no
 * document" (PC/LL Manual Parity, WS1). Mirrors the Structured Products
 * "define once, reference from the thin ticket" pattern.
 *
 * PC supports two explicit modes. Financing Arrangement defines a non-investable
 * parent plus one or more reference facilities without a trade or position.
 * Single Facility Acquisition preserves the existing full instrument + ACQUIRE
 * path via POST /api/private-credit/loans/define.
 *
 * LL (FRD v1.3 LL-FR-1, AXP-122): define is REFERENCE DATA ONLY — deal +
 * tranche + security + covenants + ratings via POST /api/credit/ll/deals/define.
 * No trade or position is created and no portfolio is selected (FR-1.4);
 * exposure is acquired later through explicit trade tickets.
 *
 * The all-in rate is intentionally NOT an input. LL reference definition stores
 * governed terms without inventing a fixing; PC recomputes its current all-in
 * server-side. The covenant section is required: the user
 * adds ≥1 covenant or explicitly acknowledges cov-lite (R1.2) — a manual loan
 * is never a thin shell.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, CheckCircle2, Landmark, BookOpen, Database, Ticket } from 'lucide-react';
import { leveragedLoanTicketPath, loan360Path, securityMasterPath } from '../../utils/loanNavigation';

import { DateField } from '../shared/DateField';

interface DefineCreditLoanFormProps {
  initialAssetClass?: string;
  openNewOnMount?: boolean;
}

interface CovenantRow {
  covenantName: string;
  metricType: string;
  direction: 'MAX' | 'MIN';
  thresholdValue: string;
  measurementFrequency: string;
}

interface LLTrancheRow {
  key: string;
  trancheCode: string;
  facilityType: string;
  loanId: string;
  loanName: string;
  cusip: string;
  isin: string;
  lstaId: string;
  wsoLoanId: string;
  seniority: string;
  originalSize: string;
  currency: string;
  baseRate: string;
  spreadBps: string;
  csaBps: string;
  floorRate: string;
  floorBasis: 'ADJUSTED_INDEX' | 'RAW_INDEX';
  oidPrice: string;
  commitmentFeeBps: string;
  tickingFeeBps: string;
  closingDate: string;
  maturityDate: string;
  availabilityEndDate: string;
}

interface PCFacilityRow {
  key: string;
  facilityCode: string;
  facilityType: string;
  loanId: string;
  loanName: string;
  cusip: string;
  seniority: string;
  originalFacilityCapacity: string;
  currentFacilityCapacity: string;
  globalOutstanding: string;
  commitmentCurrency: string;
  facilityState: string;
  baseRate: string;
  spreadBps: string;
  floorRate: string;
  maturityDate: string;
  availabilityEndDate: string;
}

interface PCRelationshipRow {
  key: string;
  fromFacilityCode: string;
  toFacilityCode: string;
  relationshipType: string;
  relationshipScope: 'BORROWER_CONTRACTUAL' | 'LENDER_ECONOMIC';
}

interface PortfolioOpt { id: number; label: string; }

const PC_FACILITY_TYPES = [
  'FIRST_LIEN', 'SECOND_LIEN', 'UNITRANCHE', 'MEZZANINE', 'REVOLVER', 'DELAYED_DRAW', 'HOLDCO_PIK',
];
const LL_FACILITY_TYPES = [
  'TERM_LOAN_B', 'TERM_LOAN_A', 'TERM_LOAN_C', 'REVOLVER', 'DELAYED_DRAW', 'BRIDGE',
];
const SENIORITIES = ['SENIOR_SECURED', 'SENIOR_UNSECURED', 'SUBORDINATED', 'JUNIOR'];
const BASE_RATES = ['Term SOFR', 'Daily SOFR', 'SOFR', 'Prime', 'Fixed'];
// Slice 8 — commitment currency options. The facility commitment (and its whole
// strip lifecycle) is denominated in one of these; no FX conversion is applied.
const COMMITMENT_CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD'];
const METRIC_TYPES = [
  'TOTAL_LEVERAGE', 'SENIOR_LEVERAGE', 'NET_LEVERAGE', 'INTEREST_COVERAGE',
  'FIXED_CHARGE', 'DSCR', 'FCCR', 'ICR', 'MINIMUM_LIQUIDITY',
];
const FREQUENCIES = ['QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL', 'MONTHLY'];

const emptyCovenant = (): CovenantRow => ({
  covenantName: '', metricType: 'TOTAL_LEVERAGE', direction: 'MAX', thresholdValue: '', measurementFrequency: 'QUARTERLY',
});

const trancheCodeForType = (facilityType: string) => ({
  TERM_LOAN_B: 'TLB', TERM_LOAN_A: 'TLA', TERM_LOAN_C: 'TLC',
  REVOLVER: 'RCF', DELAYED_DRAW: 'DDTL', BRIDGE: 'BRIDGE',
}[facilityType] || facilityType);

const emptyLlTranche = (facilityType = 'TERM_LOAN_B'): LLTrancheRow => ({
  key: `${Date.now()}-${Math.random()}`,
  trancheCode: trancheCodeForType(facilityType),
  facilityType,
  loanId: '', loanName: '', cusip: '', isin: '', lstaId: '', wsoLoanId: '',
  seniority: 'SENIOR_SECURED', originalSize: '', currency: 'USD', baseRate: 'Term SOFR',
  spreadBps: '', csaBps: '', floorRate: '', floorBasis: 'ADJUSTED_INDEX', oidPrice: '',
  commitmentFeeBps: '', tickingFeeBps: '', closingDate: '', maturityDate: '',
  availabilityEndDate: '',
});

const pcCodeForType = (facilityType: string) => ({
  FIRST_LIEN: 'FL', SECOND_LIEN: 'SL', UNITRANCHE: 'UT', MEZZANINE: 'MEZZ',
  REVOLVER: 'RCF', DELAYED_DRAW: 'DDTL', HOLDCO_PIK: 'HOLDCO',
}[facilityType] || facilityType);

const emptyPcFacility = (facilityType = 'FIRST_LIEN'): PCFacilityRow => ({
  key: `${Date.now()}-${Math.random()}`,
  facilityCode: pcCodeForType(facilityType), facilityType,
  loanId: '', loanName: '', cusip: '', seniority: 'SENIOR_SECURED',
  originalFacilityCapacity: '', currentFacilityCapacity: '', globalOutstanding: '',
  commitmentCurrency: 'USD', facilityState: 'EXPECTED', baseRate: 'Term SOFR',
  spreadBps: '', floorRate: '', maturityDate: '', availabilityEndDate: '',
});

const DefineCreditLoanForm: React.FC<DefineCreditLoanFormProps> = ({ initialAssetClass }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    borrowerName: '', loanName: '', loanId: '', cusip: '', dealCode: '', governingLaw: '',
    sponsorName: '', sector: '', industry: '',
    agentBank: '',
    facilityType: 'FIRST_LIEN', seniority: 'SENIOR_SECURED',
    originalCommitment: '', commitmentCurrency: 'USD', baseRate: 'Term SOFR', spreadBps: '', csaBps: '', floorRate: '',
    oidPrice: '', upfrontFeeBps: '',
    pikToggle: '', pikSpreadBps: '', pikPortionPct: '',
    commitmentFeeBps: '', tickingFeeBps: '', ddtlExpiry: '',
    closingDate: '', maturityDate: '', firstPaymentDate: '',
    spRating: '', moodysRating: '', fitchRating: '',
    portfolioId: '',
  });
  const [covenants, setCovenants] = useState<CovenantRow[]>([emptyCovenant()]);
  const [llTranches, setLlTranches] = useState<LLTrancheRow[]>([emptyLlTranche()]);
  const [pcFacilities, setPcFacilities] = useState<PCFacilityRow[]>([emptyPcFacility()]);
  const [pcRelationships, setPcRelationships] = useState<PCRelationshipRow[]>([]);
  const [pcDefinitionMode, setPcDefinitionMode] = useState<'ARRANGEMENT' | 'ACQUISITION'>('ARRANGEMENT');
  const [covLite, setCovLite] = useState(false);
  const [portfolios, setPortfolios] = useState<PortfolioOpt[]>([]);
  const [portfoliosError, setPortfoliosError] = useState(false);
  const [portfoliosLoading, setPortfoliosLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);

  // Asset class is user-selectable via the radio below (seeded from the URL param,
  // defaulting to PC). This drives which endpoint the form posts to.
  const [assetClass, setAssetClass] = useState<'PC' | 'LL'>(
    (initialAssetClass || '').toUpperCase() === 'LL' ? 'LL' : 'PC'
  );
  const isLeveraged = assetClass === 'LL';
  const isPcArrangement = assetClass === 'PC' && pcDefinitionMode === 'ARRANGEMENT';

  useEffect(() => {
    setForm(f => {
      if (assetClass === 'LL' && !LL_FACILITY_TYPES.includes(f.facilityType)) {
        return { ...f, facilityType: 'TERM_LOAN_B' };
      }
      if (assetClass === 'PC' && !PC_FACILITY_TYPES.includes(f.facilityType)) {
        return { ...f, facilityType: 'FIRST_LIEN' };
      }
      return f;
    });
  }, [assetClass]);

  // Load portfolios with a bounded retry. A single failed fetch used to set the
  // list to [] silently, which stranded the dropdown empty (e.g. during a
  // backend blip) and forced the user onto the DRAFT block-trade branch without
  // any signal. Now we retry, and surface an explicit error + Retry if it stays
  // down so the empty dropdown is never mistaken for "no portfolios" (MF-2).
  const loadPortfolios = useCallback(async (attempt = 0) => {
    setPortfoliosLoading(true);
    try {
      const res = await axios.get('/api/dimensions/portfolios');
      const rows = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setPortfolios(rows.map((p: any) => ({
        id: p.id,
        label: p.portfolioCode || p.portfolioName || `Portfolio ${p.id}`,
      })));
      setPortfoliosError(false);
      setPortfoliosLoading(false);
    } catch {
      if (attempt < 2) {
        setTimeout(() => loadPortfolios(attempt + 1), 1500 * (attempt + 1));
      } else {
        setPortfolios([]);
        setPortfoliosError(true);
        setPortfoliosLoading(false);
      }
    }
  }, []);

  useEffect(() => { loadPortfolios(); }, [loadPortfolios]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const setCov = (i: number, patch: Partial<CovenantRow>) =>
    setCovenants(cs => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addCov = () => { setCovLite(false); setCovenants(cs => [...cs, emptyCovenant()]); };
  const removeCov = (i: number) => setCovenants(cs => (cs.length > 1 ? cs.filter((_, idx) => idx !== i) : cs));
  const setTranche = (key: string, patch: Partial<LLTrancheRow>) =>
    setLlTranches(rows => rows.map(row => row.key === key ? { ...row, ...patch } : row));
  const setTrancheType = (key: string, facilityType: string) =>
    setTranche(key, { facilityType, trancheCode: trancheCodeForType(facilityType) });
  const addTranche = () => {
    const used = new Set(llTranches.map(row => row.facilityType));
    const nextType = LL_FACILITY_TYPES.find(type => !used.has(type)) || 'TERM_LOAN_B';
    setLlTranches(rows => [...rows, emptyLlTranche(nextType)]);
  };
  const removeTranche = (key: string) =>
    setLlTranches(rows => rows.length > 1 ? rows.filter(row => row.key !== key) : rows);
  const setPcFacility = (key: string, patch: Partial<PCFacilityRow>) => {
    const previousCode = pcFacilities.find(row => row.key === key)?.facilityCode;
    setPcFacilities(rows => rows.map(row => row.key === key ? { ...row, ...patch } : row));
    if (patch.facilityCode != null && previousCode && patch.facilityCode !== previousCode) {
      setPcRelationships(rows => rows.map(row => ({
        ...row,
        fromFacilityCode: row.fromFacilityCode === previousCode ? patch.facilityCode! : row.fromFacilityCode,
        toFacilityCode: row.toFacilityCode === previousCode ? patch.facilityCode! : row.toFacilityCode,
      })));
    }
  };
  const setPcFacilityType = (key: string, facilityType: string) =>
    setPcFacility(key, { facilityType, facilityCode: pcCodeForType(facilityType) });
  const addPcFacility = () => {
    const used = new Set(pcFacilities.map(row => row.facilityType));
    const nextType = PC_FACILITY_TYPES.find(type => !used.has(type)) || 'FIRST_LIEN';
    setPcFacilities(rows => [...rows, emptyPcFacility(nextType)]);
  };
  const removePcFacility = (key: string) => {
    if (pcFacilities.length === 1) return;
    const removed = pcFacilities.find(row => row.key === key)?.facilityCode;
    setPcFacilities(rows => rows.filter(row => row.key !== key));
    if (removed) {
      setPcRelationships(relationships => relationships.filter(
        relationship => relationship.fromFacilityCode !== removed && relationship.toFacilityCode !== removed));
    }
  };
  const setPcRelationship = (key: string, patch: Partial<PCRelationshipRow>) =>
    setPcRelationships(rows => rows.map(row => row.key === key ? { ...row, ...patch } : row));
  const addPcRelationship = () => setPcRelationships(rows => [...rows, {
    key: `${Date.now()}-${Math.random()}`,
    fromFacilityCode: pcFacilities[0]?.facilityCode || '',
    toFacilityCode: pcFacilities[1]?.facilityCode || '',
    relationshipType: 'SHARES_COLLATERAL_WITH',
    relationshipScope: 'BORROWER_CONTRACTUAL',
  }]);

  const filledCovenants = useMemo(
    () => covenants.filter(c => c.covenantName.trim() && c.thresholdValue !== ''),
    [covenants],
  );

  const submit = async () => {
    setError(null);
    setResult(null);
    if (!form.borrowerName.trim()) { setError('Borrower name is required'); return; }
    if (isLeveraged && !form.dealCode.trim()) { setError('Deal code is required'); return; }
    if (isPcArrangement && !form.dealCode.trim()) { setError('Arrangement code is required'); return; }
    if (!isLeveraged && !isPcArrangement && (!form.originalCommitment || Number(form.originalCommitment) <= 0)) {
      setError('Original commitment must be > 0'); return;
    }
    if (isLeveraged) {
      const codes = llTranches.map(row => row.trancheCode.trim().toUpperCase());
      if (codes.some(code => !code)) { setError('Every tranche requires a tranche code'); return; }
      if (new Set(codes).size !== codes.length) { setError('Tranche codes must be unique within a deal'); return; }
      const invalid = llTranches.find(row => !row.originalSize || Number(row.originalSize) <= 0);
      if (invalid) { setError(`${invalid.trancheCode || 'Each tranche'} original size must be > 0`); return; }
      const invalidOid = llTranches.find(row => row.oidPrice && Number(row.oidPrice) <= 0);
      if (invalidOid) { setError(`${invalidOid.trancheCode} OID must be positive or blank`); return; }
    }
    if (isPcArrangement) {
      const codes = pcFacilities.map(row => row.facilityCode.trim().toUpperCase());
      if (codes.some(code => !code)) { setError('Every facility requires a facility code'); return; }
      if (new Set(codes).size !== codes.length) { setError('Facility codes must be unique within an arrangement'); return; }
      const invalid = pcFacilities.find(row => !row.originalFacilityCapacity || Number(row.originalFacilityCapacity) <= 0);
      if (invalid) { setError(`${invalid.facilityCode || 'Each facility'} contractual capacity must be > 0`); return; }
      const invalidRelationship = pcRelationships.find(row =>
        !codes.includes(row.fromFacilityCode.trim().toUpperCase())
        || !codes.includes(row.toFacilityCode.trim().toUpperCase())
        || row.fromFacilityCode.trim().toUpperCase() === row.toFacilityCode.trim().toUpperCase());
      if (invalidRelationship) { setError('Each relationship must connect two distinct facilities in this arrangement'); return; }
    }
    if (filledCovenants.length === 0 && !covLite) {
      setError('Add at least one covenant, or check "Cov-lite — no maintenance covenant".'); return;
    }
    const covenantNames = filledCovenants.map(row => row.covenantName.trim().replace(/\s+/g, ' ').toUpperCase());
    if (new Set(covenantNames).size !== covenantNames.length) {
      setError('Covenant names must be unique within the reviewed agreement.'); return;
    }
    if (!isLeveraged && !isPcArrangement && form.oidPrice && Number(form.oidPrice) <= 0) {
      setError('OID price must be a positive number (e.g. 99.5), or leave blank for par (100).'); return;
    }

    const covenantsPayload = covLite ? [] : filledCovenants.map(c => ({
      covenant_name: c.covenantName.trim(),
      metric_type: c.metricType,
      covenant_direction: c.direction,
      threshold_value: Number(c.thresholdValue),
      measurement_frequency: c.measurementFrequency,
      covenant_type: 'FINANCIAL',
    }));
    const payload: any = isLeveraged ? {
      dealCode: form.dealCode.trim(),
      dealName: form.loanName.trim() || null,
      borrowerName: form.borrowerName.trim(),
      sponsorName: form.sponsorName.trim() || null,
      agentBank: form.agentBank.trim() || null,
      governingLaw: form.governingLaw.trim() || null,
      agreementDate: form.closingDate || null,
      sector: form.sector.trim() || null,
      industry: form.industry.trim() || null,
      spRating: form.spRating.trim() || null,
      moodysRating: form.moodysRating.trim() || null,
      fitchRating: form.fitchRating.trim() || null,
      covLiteAcknowledged: covLite,
      covenants: covenantsPayload,
      tranches: llTranches.map(row => ({
        trancheCode: row.trancheCode.trim().toUpperCase(),
        facilityType: row.facilityType,
        loanId: row.loanId.trim() || null,
        loanName: row.loanName.trim() || null,
        cusip: row.cusip.trim() || null,
        isin: row.isin.trim() || null,
        lstaId: row.lstaId.trim() || null,
        wsoLoanId: row.wsoLoanId.trim() || null,
        seniority: row.seniority,
        originalSize: Number(row.originalSize),
        currency: row.currency,
        baseRate: row.baseRate,
        spreadBps: row.spreadBps ? Number(row.spreadBps) : null,
        csaBps: row.csaBps ? Number(row.csaBps) : null,
        floorRate: row.floorRate ? Number(row.floorRate) : null,
        floorBasis: row.floorBasis,
        oidPrice: row.oidPrice ? Number(row.oidPrice) : null,
        commitmentFeeBps: row.facilityType === 'REVOLVER' && row.commitmentFeeBps
          ? Number(row.commitmentFeeBps) : null,
        tickingFeeBps: row.facilityType === 'DELAYED_DRAW' && row.tickingFeeBps
          ? Number(row.tickingFeeBps) : null,
        closingDate: row.closingDate || form.closingDate || null,
        maturityDate: row.maturityDate || null,
        availabilityEndDate: row.availabilityEndDate || null,
      })),
    } : isPcArrangement ? {
      arrangementCode: form.dealCode.trim(),
      arrangementName: form.loanName.trim() || `${form.borrowerName.trim()} Financing Arrangement`,
      borrowerName: form.borrowerName.trim(),
      sponsorName: form.sponsorName.trim() || null,
      agreementDate: form.closingDate || null,
      closingDate: form.closingDate || null,
      governingLaw: form.governingLaw.trim() || null,
      currency: form.commitmentCurrency || 'USD',
      arrangementStatus: form.closingDate ? 'ACTIVE' : 'EXPECTED',
      source: 'MANUAL',
      sourceRef: `manual:${form.dealCode.trim()}`,
      idempotencyKey: `manual:${form.dealCode.trim()}`,
      covLiteAcknowledged: covLite,
      covenants: covenantsPayload,
      relationships: pcRelationships.map(row => ({
        fromFacilityCode: row.fromFacilityCode.trim().toUpperCase(),
        toFacilityCode: row.toFacilityCode.trim().toUpperCase(),
        relationshipType: row.relationshipType,
        relationshipScope: row.relationshipScope,
        relationshipStatus: 'VERIFIED',
        sourceRef: `manual:${form.dealCode.trim()}`,
      })),
      facilities: pcFacilities.map(row => ({
        facilityCode: row.facilityCode.trim().toUpperCase(),
        facilityType: row.facilityType,
        loanId: row.loanId.trim() || null,
        loanName: row.loanName.trim() || null,
        cusip: row.cusip.trim() || null,
        seniority: row.seniority,
        facilityState: row.facilityState,
        commitmentCurrency: row.commitmentCurrency,
        originalFacilityCapacity: Number(row.originalFacilityCapacity),
        currentFacilityCapacity: row.currentFacilityCapacity
          ? Number(row.currentFacilityCapacity) : Number(row.originalFacilityCapacity),
        globalOutstanding: row.globalOutstanding ? Number(row.globalOutstanding) : null,
        capacityAsOf: row.globalOutstanding ? getTenantToday() : null,
        capacitySource: row.globalOutstanding ? 'MANUAL_EVIDENCE' : null,
        closingDate: form.closingDate || null,
        maturityDate: row.maturityDate || null,
        availabilityEndDate: row.availabilityEndDate || null,
        reborrowable: row.facilityType === 'REVOLVER',
        baseRate: row.baseRate,
        spreadBps: row.spreadBps ? Number(row.spreadBps) : null,
        floorRate: row.floorRate ? Number(row.floorRate) : null,
        sector: form.sector.trim() || null,
        industry: form.industry.trim() || null,
        spRating: form.spRating.trim() || null,
        moodysRating: form.moodysRating.trim() || null,
        fitchRating: form.fitchRating.trim() || null,
      })),
    } : {
      borrowerName: form.borrowerName.trim(),
      loanName: form.loanName.trim() || null,
      loanId: form.loanId.trim() || null,
      cusip: form.cusip.trim() || null,
      sponsorName: form.sponsorName.trim() || null,
      agentBank: form.agentBank.trim() || null,
      sector: form.sector.trim() || null,
      industry: form.industry.trim() || null,
      facilityType: form.facilityType,
      seniority: form.seniority,
      originalCommitment: Number(form.originalCommitment),
      // Slice 8: commitment currency — the whole strip lifecycle books in it. No FX.
      commitmentCurrency: form.commitmentCurrency || 'USD',
      baseRate: form.baseRate,
      spreadBps: form.spreadBps ? Number(form.spreadBps) : null,
      csaBps: form.csaBps ? Number(form.csaBps) : null,
      floorRate: form.floorRate ? Number(form.floorRate) : null,
      oidPrice: form.oidPrice ? Number(form.oidPrice) : null,
      upfrontFeeBps: form.upfrontFeeBps ? Number(form.upfrontFeeBps) : null,
      // F-PCP-005: PIK terms, when the agreement provides them (blank = cash pay, no PIK election possible).
      pikToggle: form.pikToggle || null,
      pikSpreadBps: form.pikToggle && form.pikSpreadBps ? Number(form.pikSpreadBps) : null,
      pikPortionPct: form.pikToggle === 'PARTIAL_PIK' && form.pikPortionPct ? Number(form.pikPortionPct) : null,
      // Slice 1e/1f — RCF/DDTL fee terms (only meaningful for a revolver/DDTL).
      commitmentFeeBps: form.facilityType === 'REVOLVER' && form.commitmentFeeBps
        ? Number(form.commitmentFeeBps) : null,
      tickingFeeBps: form.facilityType === 'DELAYED_DRAW' && form.tickingFeeBps
        ? Number(form.tickingFeeBps) : null,
      ddtlExpiry: form.facilityType === 'DELAYED_DRAW' && form.ddtlExpiry
        ? form.ddtlExpiry : null,
      closingDate: form.closingDate || null,
      maturityDate: form.maturityDate || null,
      firstPaymentDate: form.firstPaymentDate || null,
      spRating: form.spRating.trim() || null,
      moodysRating: form.moodysRating.trim() || null,
      fitchRating: form.fitchRating.trim() || null,
      covLiteAcknowledged: covLite,
      covenants: covenantsPayload,
    };
    // FR-1.4 (AXP-122): the LL define is reference-only — it must NOT send a
    // portfolioId. PC keeps the optional portfolio (booked ACQUIRE / DRAFT block).
    if (!isLeveraged && !isPcArrangement) {
      payload.portfolioId = form.portfolioId ? Number(form.portfolioId) : null;
    }

    setSubmitting(true);
    try {
      const url = isLeveraged
        ? '/api/credit/ll/deals/define'
        : isPcArrangement
          ? '/api/private-credit/arrangements/reference'
          : '/api/private-credit/loans/define';
      const { data } = await axios.post(url, payload);
      setResult(data);
    } catch (err: any) {
      // #88: GlobalExceptionHandler's ApiError body uses `message` (+ errorCode);
      // read it first so the real reason surfaces instead of the bare axios string.
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to define loan');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const arrangementFacilities = result.arrangementCode && Array.isArray(result.facilities)
      ? result.facilities : null;
    return (
      <Wrap>
        <Success>
          <CheckCircle2 size={40} className="text-emerald-400" />
          <h3>{result.tranches ? 'Credit deal defined' : arrangementFacilities ? 'Financing arrangement defined' : 'Loan defined'}</h3>
          <p>
            {result.message || 'Private credit loan created.'}<br />
            {result.tranches
              ? `${result.dealCode || `Deal #${result.dealId}`} | ${result.tranches.length} tranche(s) | ${result.covenantsCreated} covenant change(s)`
              : arrangementFacilities
                ? `${result.arrangementCode} | ${arrangementFacilities.length} facility(s) | reference data only | no trade or position created`
              : `CUSIP ${result.cusip} | security #${result.securityId} | ${result.covLite ? 'cov-lite acknowledged' : `${result.covenantsCreated} covenant(s)`} | ${result.ratingsCreated} rating(s) | ${result.referenceOnly ? 'reference data only' : result.tradeStatus}`}
          </p>
          {Array.isArray(result.tranches) && (
            <ResultTable>
              <thead><tr><th>Tranche</th><th>Type</th><th>CUSIP</th><th>State</th><th>Actions</th></tr></thead>
              <tbody>
                {result.tranches.map((tranche: any) => (
                  <tr key={tranche.loanRecordId}>
                    <td>{tranche.trancheCode}</td>
                    <td>{String(tranche.facilityType || '').replace(/_/g, ' ')}</td>
                    <td>{tranche.cusip || '-'}</td>
                    <td>{tranche.facilityState || '-'}</td>
                    <td><ResultActions>
                      <IconBtn title="Open Loan 360" onClick={() => navigate(loan360Path('LEVERAGED_LOAN', tranche.loanRecordId))}><BookOpen size={15} /></IconBtn>
                      {tranche.cusip && <IconBtn title="Open Security Master" onClick={() => navigate(securityMasterPath(tranche.cusip))}><Database size={15} /></IconBtn>}
                      <IconBtn title="Create acquisition ticket" onClick={() => navigate(leveragedLoanTicketPath(tranche.cusip))}><Ticket size={15} /></IconBtn>
                    </ResultActions></td>
                  </tr>
                ))}
              </tbody>
            </ResultTable>
          )}
          {arrangementFacilities && (
            <ResultTable>
              <thead><tr><th>Facility</th><th>Type</th><th>Contractual Capacity</th><th>IBOR Exposure</th><th>Actions</th></tr></thead>
              <tbody>{arrangementFacilities.map((facility: any) => (
                <tr key={facility.id}>
                  <td>{facility.facilityCode}</td>
                  <td>{String(facility.facilityType || '').replace(/_/g, ' ')}</td>
                  <td>{facility.commitmentCurrency || ''} {Number(facility.currentFacilityCapacity || 0).toLocaleString()}</td>
                  <td>{Number(facility.officialPortfolioExposure?.fundedExposure || 0).toLocaleString()}</td>
                  <td><ResultActions>
                    <IconBtn title="Open Loan 360" onClick={() => navigate(loan360Path('PRIVATE_CREDIT', facility.id))}><BookOpen size={15} /></IconBtn>
                  </ResultActions></td>
                </tr>
              ))}</tbody>
            </ResultTable>
          )}
          <BtnRow>
            {!result.tranches && result.referenceOnly && result.loanRecordId && (
              <Btn onClick={() => navigate(loan360Path('LEVERAGED_LOAN', result.loanRecordId))}>
                <BookOpen size={15} /> Open Loan 360
              </Btn>
            )}
            {!result.tranches && result.cusip && (
              <Btn onClick={() => navigate(securityMasterPath(result.cusip))}>
                <Database size={15} /> Open Security Master
              </Btn>
            )}
            {!result.tranches && result.referenceOnly && (
              <Btn onClick={() => navigate(leveragedLoanTicketPath(result.cusip))}>
                <Ticket size={15} /> Create Acquisition Ticket
              </Btn>
            )}
            <Btn onClick={() => { setResult(null); }}>Define another</Btn>
          </BtnRow>
        </Success>
      </Wrap>
    );
  }

  const facilityTypes = isLeveraged ? LL_FACILITY_TYPES : PC_FACILITY_TYPES;
  const preventWheelMutation = (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur();

  return (
    <Wrap>
      <HeaderRow>
        <Landmark size={20} className="text-violet-300" />
        <div>
          <h2>Define {isLeveraged ? 'Leveraged Loan' : isPcArrangement ? 'Private Credit Financing Arrangement' : 'Private Credit Loan'}</h2>
          <p>
            {isLeveraged
              ? 'Define one syndicated credit deal with every governed facility, term set, covenant and rating.'
              : isPcArrangement
                ? 'Define the legal arrangement and all borrower facilities without creating investor exposure.'
                : 'Create the bilateral/private-credit instrument — covenants and ratings included.'}
          </p>
        </div>
      </HeaderRow>

      {/* Asset-class selector — PC (bilateral) vs LL (syndicated). Drives the endpoint. */}
      <div style={{ display: 'flex', gap: '1.5rem', margin: '0.25rem 0 1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Asset Class</span>
        <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', cursor: 'pointer', color: '#e2e8f0' }}>
          <input type="radio" name="loanAssetClass" checked={assetClass === 'PC'} onChange={() => setAssetClass('PC')} />
          Private Credit
        </label>
        <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', cursor: 'pointer', color: '#e2e8f0' }}>
          <input type="radio" name="loanAssetClass" checked={assetClass === 'LL'} onChange={() => setAssetClass('LL')} />
          Leveraged Loan
        </label>
      </div>

      {!isLeveraged && (
        <ModeSwitch aria-label="Private credit definition mode">
          <button type="button" className={isPcArrangement ? 'active' : ''} onClick={() => setPcDefinitionMode('ARRANGEMENT')}>Financing Arrangement</button>
          <button type="button" className={!isPcArrangement ? 'active' : ''} onClick={() => setPcDefinitionMode('ACQUISITION')}>Single Facility Acquisition</button>
        </ModeSwitch>
      )}

      <Note>
        {isLeveraged
          ? 'Leveraged loan — reference data only. This screen must not create a trade, portfolio assignment, '
            + 'position, lot, or cash. Investor exposure is acquired later through Primary Allocation or Secondary Assignment.'
          : isPcArrangement
            ? 'Reference data only. The arrangement is non-investable; each facility receives its own security identity, while trades and official positions remain facility-level.'
            : 'Private credit loan — books the full instrument (SMF + covenants + ratings + a booked ACQUIRE) through the unified posting path.'}
      </Note>

      <SectionTitle>Identification</SectionTitle>
      <Grid>
        <FG><L>Borrower Name *</L><I value={form.borrowerName} onChange={e => set('borrowerName', e.target.value)} placeholder="e.g. Project Atlas Holdings" /></FG>
        <FG><L>{isLeveraged ? 'Deal Name' : isPcArrangement ? 'Arrangement Name' : 'Loan Name'}</L><I value={form.loanName} onChange={e => set('loanName', e.target.value)} placeholder={isLeveraged || isPcArrangement ? 'e.g. Atlas Credit Agreement' : 'e.g. Atlas First Lien'} /></FG>
        {isLeveraged || isPcArrangement ? (
          <>
            <FG><L>{isPcArrangement ? 'Arrangement Code' : 'Deal Code'} *</L><I value={form.dealCode} onChange={e => set('dealCode', e.target.value)} placeholder="e.g. ATLAS-2026" /></FG>
            <FG><L>Agreement / Closing Date</L><DateField value={form.closingDate ?? ''} onChange={iso => set('closingDate', iso)} /></FG>
            <FG><L>Governing Law</L><I value={form.governingLaw} onChange={e => set('governingLaw', e.target.value)} placeholder="e.g. New York" /></FG>
          </>
        ) : (
          <>
            <FG><L>Loan ID</L><I value={form.loanId} onChange={e => set('loanId', e.target.value)} placeholder="optional - auto if blank" /></FG>
            <FG><L>CUSIP</L><I value={form.cusip} onChange={e => set('cusip', e.target.value)} placeholder="optional - synthetic if blank" /></FG>
          </>
        )}
        <FG>
          <L>Sponsor</L>
          <I value={form.sponsorName} onChange={e => set('sponsorName', e.target.value)} placeholder="e.g. Vista Equity" />
        </FG>
        {isLeveraged && (
          <FG><L>Administrative Agent</L><I value={form.agentBank} onChange={e => set('agentBank', e.target.value)} placeholder="e.g. Bank of America, N.A." /></FG>
        )}
        <FG><L>Sector</L><I value={form.sector} onChange={e => set('sector', e.target.value)} placeholder="e.g. Software" /></FG>
        <FG><L>Industry</L><I value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. Application Software" /></FG>
      </Grid>

      <SectionTitle>{isLeveraged ? 'Facilities / Tranches' : isPcArrangement ? 'Borrower Facilities' : 'Facility & Economics'}</SectionTitle>
      {isLeveraged ? (
        <>
          {llTranches.map((tranche, index) => (
            <TrancheBand key={tranche.key}>
              <TrancheHead>
                <div><b>{tranche.trancheCode || `Tranche ${index + 1}`}</b><span>{String(tranche.facilityType).replace(/_/g, ' ')}</span></div>
                <IconBtn title="Remove tranche" onClick={() => removeTranche(tranche.key)} disabled={llTranches.length === 1}><Trash2 size={15} /></IconBtn>
              </TrancheHead>
              <Grid>
                <FG><L>Facility Type *</L><S value={tranche.facilityType} onChange={e => setTrancheType(tranche.key, e.target.value)}>{LL_FACILITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</S></FG>
                <FG><L>Tranche Code *</L><I value={tranche.trancheCode} onChange={e => setTranche(tranche.key, { trancheCode: e.target.value })} placeholder="TLB / RCF / DDTL" /></FG>
                <FG><L>Facility Name</L><I value={tranche.loanName} onChange={e => setTranche(tranche.key, { loanName: e.target.value })} placeholder="Exact legal facility name" /></FG>
                <FG><L>Loan ID</L><I value={tranche.loanId} onChange={e => setTranche(tranche.key, { loanId: e.target.value })} placeholder="optional - derived from deal and tranche" /></FG>
                <FG><L>CUSIP</L><I value={tranche.cusip} onChange={e => setTranche(tranche.key, { cusip: e.target.value })} placeholder="optional - synthetic if blank" /></FG>
                <FG><L>ISIN</L><I value={tranche.isin} onChange={e => setTranche(tranche.key, { isin: e.target.value })} /></FG>
                <FG><L>LSTA / LoanX ID</L><I value={tranche.lstaId} onChange={e => setTranche(tranche.key, { lstaId: e.target.value })} /></FG>
                <FG><L>WSO Loan ID</L><I value={tranche.wsoLoanId} onChange={e => setTranche(tranche.key, { wsoLoanId: e.target.value })} /></FG>
                <FG><L>Seniority</L><S value={tranche.seniority} onChange={e => setTranche(tranche.key, { seniority: e.target.value })}>{SENIORITIES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</S></FG>
                <FG><L>Original Facility Size ({tranche.currency}) *</L><I type="number" value={tranche.originalSize} onWheel={preventWheelMutation} onChange={e => setTranche(tranche.key, { originalSize: e.target.value })} /></FG>
                <FG><L>Currency</L><S value={tranche.currency} onChange={e => setTranche(tranche.key, { currency: e.target.value })}>{COMMITMENT_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</S></FG>
                <FG><L>Base Rate Index</L><S value={tranche.baseRate} onChange={e => setTranche(tranche.key, { baseRate: e.target.value })}>{BASE_RATES.map(t => <option key={t} value={t}>{t}</option>)}</S></FG>
                <FG><L>Spread (bps)</L><I type="number" value={tranche.spreadBps} onWheel={preventWheelMutation} onChange={e => setTranche(tranche.key, { spreadBps: e.target.value })} /></FG>
                <FG><L>CSA (bps)</L><I type="number" value={tranche.csaBps} onWheel={preventWheelMutation} onChange={e => setTranche(tranche.key, { csaBps: e.target.value })} /></FG>
                <FG><L>Floor (%)</L><I type="number" step="0.01" value={tranche.floorRate} onWheel={preventWheelMutation} onChange={e => setTranche(tranche.key, { floorRate: e.target.value })} /></FG>
                <FG><L>Floor Basis</L><S value={tranche.floorBasis} onChange={e => setTranche(tranche.key, { floorBasis: e.target.value as LLTrancheRow['floorBasis'] })}><option value="ADJUSTED_INDEX">Adjusted Index</option><option value="RAW_INDEX">Raw Index</option></S></FG>
                <FG><L>OID / Issue Price</L><I type="number" step="0.01" value={tranche.oidPrice} onWheel={preventWheelMutation} onChange={e => setTranche(tranche.key, { oidPrice: e.target.value })} /></FG>
                {tranche.facilityType === 'REVOLVER' && <FG><L>Commitment Fee (bps)</L><I type="number" value={tranche.commitmentFeeBps} onWheel={preventWheelMutation} onChange={e => setTranche(tranche.key, { commitmentFeeBps: e.target.value })} /></FG>}
                {tranche.facilityType === 'DELAYED_DRAW' && <FG><L>Ticking Fee (bps)</L><I type="number" value={tranche.tickingFeeBps} onWheel={preventWheelMutation} onChange={e => setTranche(tranche.key, { tickingFeeBps: e.target.value })} /></FG>}
                <FG><L>Closing Date</L><DateField value={tranche.closingDate ?? ''} onChange={iso => setTranche(tranche.key, { closingDate: iso })} /></FG>
                <FG><L>Maturity Date</L><DateField value={tranche.maturityDate ?? ''} onChange={iso => setTranche(tranche.key, { maturityDate: iso })} /></FG>
                {(tranche.facilityType === 'DELAYED_DRAW' || tranche.facilityType === 'REVOLVER') && <FG><L>Availability End Date</L><DateField value={tranche.availabilityEndDate ?? ''} onChange={iso => setTranche(tranche.key, { availabilityEndDate: iso })} /></FG>}
                <FG><L>Portfolio Exposure</L><ReadOnly>Zero until an acquisition is posted through the existing tranche ticket</ReadOnly></FG>
              </Grid>
            </TrancheBand>
          ))}
          <Btn onClick={addTranche}><Plus size={14} /> Add tranche</Btn>
        </>
      ) : isPcArrangement ? (
        <>
          {pcFacilities.map((facility, index) => (
            <TrancheBand key={facility.key}>
              <TrancheHead>
                <div><b>{facility.facilityCode || `Facility ${index + 1}`}</b><span>{facility.facilityType.replace(/_/g, ' ')}</span></div>
                <IconBtn title="Remove facility" onClick={() => removePcFacility(facility.key)} disabled={pcFacilities.length === 1}><Trash2 size={15} /></IconBtn>
              </TrancheHead>
              <Grid>
                <FG><L>Facility Type *</L><S value={facility.facilityType} onChange={e => setPcFacilityType(facility.key, e.target.value)}>{PC_FACILITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</S></FG>
                <FG><L>Facility Code *</L><I value={facility.facilityCode} onChange={e => setPcFacility(facility.key, { facilityCode: e.target.value })} placeholder="FL / RCF / DDTL" /></FG>
                <FG><L>Facility Name</L><I value={facility.loanName} onChange={e => setPcFacility(facility.key, { loanName: e.target.value })} placeholder="Exact legal facility name" /></FG>
                <FG><L>Loan ID</L><I value={facility.loanId} onChange={e => setPcFacility(facility.key, { loanId: e.target.value })} placeholder="optional - derived from arrangement" /></FG>
                <FG><L>CUSIP</L><I value={facility.cusip} onChange={e => setPcFacility(facility.key, { cusip: e.target.value })} placeholder="optional - synthetic if blank" /></FG>
                <FG><L>Seniority</L><S value={facility.seniority} onChange={e => setPcFacility(facility.key, { seniority: e.target.value })}>{SENIORITIES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</S></FG>
                <FG><L>Original Contractual Capacity *</L><I type="number" value={facility.originalFacilityCapacity} onWheel={preventWheelMutation} onChange={e => setPcFacility(facility.key, { originalFacilityCapacity: e.target.value })} /></FG>
                <FG><L>Current Contractual Capacity</L><I type="number" value={facility.currentFacilityCapacity} onWheel={preventWheelMutation} onChange={e => setPcFacility(facility.key, { currentFacilityCapacity: e.target.value })} placeholder="defaults to original" /></FG>
                <FG><L>Global Outstanding (evidenced)</L><I type="number" value={facility.globalOutstanding} onWheel={preventWheelMutation} onChange={e => setPcFacility(facility.key, { globalOutstanding: e.target.value })} /></FG>
                <FG><L>Currency</L><S value={facility.commitmentCurrency} onChange={e => setPcFacility(facility.key, { commitmentCurrency: e.target.value })}>{COMMITMENT_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</S></FG>
                <FG><L>Facility State</L><S value={facility.facilityState} onChange={e => setPcFacility(facility.key, { facilityState: e.target.value })}><option value="EXPECTED">Expected</option><option value="NOT_EFFECTIVE">Not Effective</option><option value="ACTIVE">Active</option></S></FG>
                <FG><L>Base Rate Index</L><S value={facility.baseRate} onChange={e => setPcFacility(facility.key, { baseRate: e.target.value })}>{BASE_RATES.map(t => <option key={t} value={t}>{t}</option>)}</S></FG>
                <FG><L>Spread (bps)</L><I type="number" value={facility.spreadBps} onWheel={preventWheelMutation} onChange={e => setPcFacility(facility.key, { spreadBps: e.target.value })} /></FG>
                <FG><L>Floor (%)</L><I type="number" step="0.01" value={facility.floorRate} onWheel={preventWheelMutation} onChange={e => setPcFacility(facility.key, { floorRate: e.target.value })} /></FG>
                <FG><L>Maturity Date</L><DateField value={facility.maturityDate ?? ''} onChange={iso => setPcFacility(facility.key, { maturityDate: iso })} /></FG>
                {(facility.facilityType === 'DELAYED_DRAW' || facility.facilityType === 'REVOLVER') && <FG><L>Availability End Date</L><DateField value={facility.availabilityEndDate ?? ''} onChange={iso => setPcFacility(facility.key, { availabilityEndDate: iso })} /></FG>}
                <FG><L>Portfolio Exposure</L><ReadOnly>Zero until an acquisition is posted through the facility's trade lifecycle</ReadOnly></FG>
              </Grid>
            </TrancheBand>
          ))}
          <BtnRow style={{ justifyContent: 'flex-start' }}>
            <Btn onClick={addPcFacility}><Plus size={14} /> Add facility</Btn>
            {pcFacilities.length > 1 && <Btn onClick={addPcRelationship}><Plus size={14} /> Add relationship</Btn>}
          </BtnRow>
          {pcRelationships.length > 0 && (
            <RelationshipBand>
              <L>Explicit Facility Relationships</L>
              {pcRelationships.map(relationship => (
                <RelationshipGrid key={relationship.key}>
                  <S value={relationship.fromFacilityCode} onChange={e => setPcRelationship(relationship.key, { fromFacilityCode: e.target.value })}>
                    {pcFacilities.map(facility => <option key={facility.key} value={facility.facilityCode}>{facility.facilityCode || 'Unnamed facility'}</option>)}
                  </S>
                  <S value={relationship.relationshipType} onChange={e => setPcRelationship(relationship.key, { relationshipType: e.target.value })}>
                    <option value="SHARES_COLLATERAL_WITH">Shares collateral with</option>
                    <option value="CROSS_DEFAULTS_TO">Cross-defaults to</option>
                    <option value="CROSS_ACCELERATES_TO">Cross-accelerates to</option>
                    <option value="PRIORITY_TO">Priority to</option>
                    <option value="ECONOMIC_FIRST_OUT_OF">Economic first-out of</option>
                    <option value="ECONOMIC_LAST_OUT_OF">Economic last-out of</option>
                    <option value="REFINANCES">Refinances</option>
                  </S>
                  <S value={relationship.toFacilityCode} onChange={e => setPcRelationship(relationship.key, { toFacilityCode: e.target.value })}>
                    {pcFacilities.map(facility => <option key={facility.key} value={facility.facilityCode}>{facility.facilityCode || 'Unnamed facility'}</option>)}
                  </S>
                  <S value={relationship.relationshipScope} onChange={e => setPcRelationship(relationship.key, { relationshipScope: e.target.value as PCRelationshipRow['relationshipScope'] })}>
                    <option value="BORROWER_CONTRACTUAL">Borrower contractual</option>
                    <option value="LENDER_ECONOMIC">Lender economic</option>
                  </S>
                  <IconBtn title="Remove relationship" onClick={() => setPcRelationships(rows => rows.filter(row => row.key !== relationship.key))}><Trash2 size={15} /></IconBtn>
                </RelationshipGrid>
              ))}
            </RelationshipBand>
          )}
        </>
      ) : (
      <Grid>
        <FG><L>Facility Type</L><S value={form.facilityType} onChange={e => set('facilityType', e.target.value)}>{facilityTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</S></FG>
        <FG><L>Seniority</L><S value={form.seniority} onChange={e => set('seniority', e.target.value)}>{SENIORITIES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</S></FG>
        <FG><L>{isLeveraged ? `Original Tranche Size (${form.commitmentCurrency}) *` : `Original Commitment (${form.commitmentCurrency}) *`}</L><I type="number" value={form.originalCommitment} onWheel={preventWheelMutation} onChange={e => set('originalCommitment', e.target.value)} placeholder="e.g. 75000000" /></FG>
        <FG><L>Commitment Currency</L><S value={form.commitmentCurrency} onChange={e => set('commitmentCurrency', e.target.value)}>{COMMITMENT_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</S></FG>
        <FG><L>Base Rate Index</L><S value={form.baseRate} onChange={e => set('baseRate', e.target.value)}>{BASE_RATES.map(t => <option key={t} value={t}>{t}</option>)}</S></FG>
        <FG><L>Spread (bps)</L><I type="number" value={form.spreadBps} onWheel={preventWheelMutation} onChange={e => set('spreadBps', e.target.value)} placeholder="e.g. 575" /></FG>
        <FG><L>CSA (bps)</L><I type="number" value={form.csaBps} onWheel={preventWheelMutation} onChange={e => set('csaBps', e.target.value)} placeholder="e.g. 10" /></FG>
        <FG><L>Floor (%)</L><I type="number" step="0.01" value={form.floorRate} onWheel={preventWheelMutation} onChange={e => set('floorRate', e.target.value)} placeholder="e.g. 1.00" /></FG>
        <FG><L>OID (price)</L><I type="number" step="0.01" value={form.oidPrice} onWheel={preventWheelMutation} onChange={e => set('oidPrice', e.target.value)} placeholder="e.g. 99.5" /></FG>
        <FG><L>Upfront Fee (bps)</L><I type="number" value={form.upfrontFeeBps} onWheel={preventWheelMutation} onChange={e => set('upfrontFeeBps', e.target.value)} placeholder="e.g. 200" /></FG>
        {/* F-PCP-005: PIK terms as the agreement states them; without them the facility has no PIK election. */}
        <FG><L>PIK terms</L><S value={form.pikToggle} onChange={e => set('pikToggle', e.target.value)}><option value="">None — cash pay</option><option value="PIK_TOGGLE">PIK toggle (borrower elects per period)</option><option value="PARTIAL_PIK">Partial PIK (fixed portion)</option><option value="FULL_PIK">Full PIK</option></S></FG>
        {form.pikToggle && <FG><L>PIK spread (bps)</L><I type="number" value={form.pikSpreadBps} onWheel={preventWheelMutation} onChange={e => set('pikSpreadBps', e.target.value)} placeholder="e.g. 75 (premium over cash spread)" /></FG>}
        {form.pikToggle === 'PARTIAL_PIK' && <FG><L>PIK portion (%)</L><I type="number" step="0.01" value={form.pikPortionPct} onWheel={preventWheelMutation} onChange={e => set('pikPortionPct', e.target.value)} placeholder="e.g. 50" /></FG>}
        {/* #90: RCF/DDTL fee + draw-window inputs apply to BOTH PC and LL — the
            payload already sends them by facility type, so they must render for PC
            too (a PC revolver has a commitment fee; a PC DDTL needs a ticking fee +
            draw-window expiry, without which the facility-aware drawdown modal has
            no expiry to enforce/count down). */}
        {form.facilityType === 'REVOLVER' && (
          <FG><L>Commitment Fee (bps)</L><I type="number" value={form.commitmentFeeBps} onWheel={preventWheelMutation} onChange={e => set('commitmentFeeBps', e.target.value)} placeholder="e.g. 50 (on undrawn)" /></FG>
        )}
        {form.facilityType === 'DELAYED_DRAW' && (
          <FG><L>Ticking Fee (bps)</L><I type="number" value={form.tickingFeeBps} onWheel={preventWheelMutation} onChange={e => set('tickingFeeBps', e.target.value)} placeholder="e.g. 100 (on undrawn)" /></FG>
        )}
        {form.facilityType === 'DELAYED_DRAW' && (
          <FG><L>DDTL Draw-Window Expiry</L><DateField value={form.ddtlExpiry ?? ''} onChange={iso => set('ddtlExpiry', iso)} /></FG>
        )}
        <FG>
          <L>All-In Rate</L>
          <ReadOnly>Recomputed on save (server-side)</ReadOnly>
        </FG>
      </Grid>
      )}

      {!isLeveraged && !isPcArrangement && <>
        <SectionTitle>Dates</SectionTitle>
        <Grid>
          <FG><L>Closing Date</L><DateField value={form.closingDate ?? ''} onChange={iso => set('closingDate', iso)} /></FG>
          <FG><L>Maturity Date</L><DateField value={form.maturityDate ?? ''} onChange={iso => set('maturityDate', iso)} /></FG>
          <FG><L>First Payment Date</L><DateField value={form.firstPaymentDate ?? ''} onChange={iso => set('firstPaymentDate', iso)} /></FG>
        </Grid>
      </>}

      <SectionTitle>Ratings</SectionTitle>
      <Grid>
        <FG><L>S&amp;P</L><I value={form.spRating} onChange={e => set('spRating', e.target.value)} placeholder="e.g. B+" /></FG>
        <FG><L>Moody's</L><I value={form.moodysRating} onChange={e => set('moodysRating', e.target.value)} placeholder="e.g. B1" /></FG>
        <FG><L>Fitch</L><I value={form.fitchRating} onChange={e => set('fitchRating', e.target.value)} placeholder="e.g. B" /></FG>
      </Grid>

      {/* FR-1.4 (AXP-122): LL define is reference-only — no portfolio selector.
          PC keeps the optional portfolio (booked ACQUIRE / DRAFT block trade). */}
      {!isLeveraged && !isPcArrangement && (
        <>
          <SectionTitle>Portfolio (optional — leave blank for a DRAFT block trade)</SectionTitle>
          <Grid>
            <FG>
              <L>Portfolio</L>
              <S value={form.portfolioId} onChange={e => set('portfolioId', e.target.value)}>
                <option value="">
                  {portfoliosLoading ? 'Loading portfolios…' : '— Unassigned (allocate in pre-clear) —'}
                </option>
                {portfolios.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </S>
              {portfoliosError && (
                <PortfolioWarn>
                  Couldn't load portfolios.{' '}
                  <button type="button" onClick={() => loadPortfolios()}>Retry</button>
                  {' '}— submitting now creates a DRAFT block trade you'll need to allocate later.
                </PortfolioWarn>
              )}
            </FG>
          </Grid>
        </>
      )}

      <SectionTitle>Covenants (required — add covenants or acknowledge cov-lite)</SectionTitle>
      <CovLite>
        <input type="checkbox" id="covlite" checked={covLite}
          onChange={e => setCovLite(e.target.checked)} />
        <label htmlFor="covlite">Cov-lite — no maintenance covenant (incurrence-only)</label>
      </CovLite>

      {!covLite && covenants.map((c, i) => (
        <CovRow key={i}>
          <I value={c.covenantName} onChange={e => setCov(i, { covenantName: e.target.value })} placeholder="Covenant name" />
          <S value={c.metricType} onChange={e => setCov(i, { metricType: e.target.value })}>{METRIC_TYPES.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}</S>
          <S value={c.direction} onChange={e => setCov(i, { direction: e.target.value as 'MAX' | 'MIN' })}>
            <option value="MAX">MAX (≤)</option>
            <option value="MIN">MIN (≥)</option>
          </S>
          <I type="number" step="0.01" value={c.thresholdValue} onWheel={preventWheelMutation} onChange={e => setCov(i, { thresholdValue: e.target.value })} placeholder="threshold" />
          <S value={c.measurementFrequency} onChange={e => setCov(i, { measurementFrequency: e.target.value })}>{FREQUENCIES.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}</S>
          <Btn onClick={() => removeCov(i)} disabled={covenants.length === 1} title="Remove"><Trash2 size={14} /></Btn>
        </CovRow>
      ))}
      {!covLite && (
        <Btn onClick={addCov} style={{ marginTop: 4 }}><Plus size={14} /> Add covenant</Btn>
      )}

      {error && <Err>{error}</Err>}

      <BtnRow style={{ marginTop: 20 }}>
        <Btn $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Defining…' : isLeveraged
            ? `Define Deal & ${llTranches.length} Tranche${llTranches.length === 1 ? '' : 's'}`
            : isPcArrangement
              ? `Define Arrangement & ${pcFacilities.length} Facilit${pcFacilities.length === 1 ? 'y' : 'ies'}`
              : 'Define Loan & Book Acquisition'}
        </Btn>
      </BtnRow>
    </Wrap>
  );
};

export default DefineCreditLoanForm;

// ── styled ──────────────────────────────────────────────────────────────────
const Wrap = styled.div`color: #e2e8f0;`;
const HeaderRow = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 1.25rem;
  h2 { font-size: 1.1rem; font-weight: 700; color: #fff; margin: 0; }
  p { font-size: 0.8rem; color: #94a3b8; margin: 2px 0 0; }
`;
const SectionTitle = styled.h3`
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
  color: #a78bfa; margin: 1.5rem 0 0.75rem; border-bottom: 1px solid rgba(255,255,255,0.06); padding-bottom: 6px;
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.9rem;
  @media (max-width: 1050px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 680px) { grid-template-columns: minmax(0, 1fr); }
`;
const FG = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const L = styled.label`font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8;`;
const baseInput = `
  background: rgba(15,23,42,0.8); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px;
  padding: 0.55rem 0.7rem; color: #e2e8f0; font-size: 0.88rem;
  &:focus { outline: none; border-color: rgba(139,92,246,0.5); box-shadow: 0 0 0 3px rgba(139,92,246,0.15); }
`;
const I = styled.input`${baseInput}`;
const S = styled.select`${baseInput}`;
const ReadOnly = styled.div`
  background: rgba(139,92,246,0.06); border: 1px dashed rgba(139,92,246,0.3); border-radius: 8px;
  padding: 0.55rem 0.7rem; color: #c4b5fd; font-size: 0.8rem; font-style: italic;
`;
const CovLite = styled.div`
  display: flex; align-items: center; gap: 8px; margin-bottom: 0.75rem;
  label { font-size: 0.85rem; color: #cbd5e1; }
  input { width: 16px; height: 16px; accent-color: #8b5cf6; }
`;
const CovRow = styled.div`
  display: grid; grid-template-columns: 1.6fr 1.2fr 0.9fr 0.9fr 1fr auto; gap: 8px; margin-bottom: 8px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const TrancheBand = styled.section`
  border-top: 1px solid rgba(148,163,184,0.24);
  border-bottom: 1px solid rgba(15,23,42,0.9);
  padding: 0.9rem 0 1.15rem;
  margin-bottom: 0.8rem;
`;
const TrancheHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 0.8rem;
  div { display: flex; align-items: baseline; gap: 10px; }
  b { color: #f8fafc; font-size: 0.9rem; }
  span { color: #94a3b8; font-size: 0.72rem; }
`;
const RelationshipBand = styled.div`
  margin-top: 14px; padding: 12px 0; border-top: 1px solid rgba(148,163,184,0.2);
  > label { display: block; margin-bottom: 8px; }
`;
const RelationshipGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1.4fr 1fr 1.2fr auto; gap: 8px; margin-bottom: 8px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const Note = styled.div`
  background: rgba(245,158,11,0.06); border: 1px solid rgba(245,158,11,0.25); border-radius: 8px;
  padding: 0.7rem 1rem; font-size: 0.8rem; color: #fcd34d; margin-bottom: 1rem;
`;
const ModeSwitch = styled.div`
  display: inline-flex; padding: 3px; margin: 0 0 1rem; border: 1px solid rgba(148,163,184,0.24);
  border-radius: 6px; background: rgba(15,23,42,0.72);
  button {
    min-height: 34px; padding: 0 12px; border: 0; border-radius: 4px;
    background: transparent; color: #94a3b8; font-size: 0.8rem; font-weight: 600; cursor: pointer;
  }
  button.active { background: #334155; color: #f8fafc; }
`;
const Err = styled.div`
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.3); border-radius: 8px;
  padding: 0.7rem 1rem; color: #fca5a5; font-size: 0.85rem; margin-top: 1rem;
`;
const PortfolioWarn = styled.div`
  margin-top: 6px; font-size: 0.75rem; color: #fca5a5;
  button {
    background: none; border: none; color: #93c5fd; cursor: pointer;
    text-decoration: underline; font-size: 0.75rem; padding: 0;
  }
`;
const BtnRow = styled.div`display: flex; gap: 8px; justify-content: flex-end;`;
const Btn = styled.button<{ $primary?: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 0.55rem 1.1rem; border-radius: 8px; font-weight: 600; font-size: 0.85rem; cursor: pointer; border: 1px solid;
  ${p => p.$primary
    ? `background: linear-gradient(135deg,#8b5cf6,#6366f1); border-color: rgba(139,92,246,0.5); color: #fff; &:hover{filter:brightness(1.1);}`
    : `background: transparent; border-color: rgba(255,255,255,0.1); color: #94a3b8; &:hover{background:rgba(255,255,255,0.04); color:#e2e8f0;}`}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const IconBtn = styled.button`
  width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid rgba(148,163,184,0.24); border-radius: 6px;
  background: rgba(15,23,42,0.6); color: #cbd5e1; cursor: pointer;
  &:hover { color: #fff; border-color: #60a5fa; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;
const ResultTable = styled.table`
  width: min(920px, 100%); border-collapse: collapse; text-align: left; font-size: 0.8rem;
  th { color: #94a3b8; font-size: 0.68rem; text-transform: uppercase; padding: 8px; border-bottom: 1px solid #334155; }
  td { color: #e2e8f0; padding: 8px; border-bottom: 1px solid #1e293b; }
`;
const ResultActions = styled.div`display: flex; gap: 6px;`;
const Success = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center; padding: 3rem 1rem;
  h3 { font-size: 1.2rem; font-weight: 700; color: #fff; margin: 0; }
  p { font-size: 0.9rem; color: #cbd5e1; margin: 0; line-height: 1.6; }
  b { color: #fff; }
`;
